import { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Spacer,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { useCompanySelection } from "src/store/companySelectionState";
import { useLocation } from "react-router-dom";
import { cookieStorage } from "src/utils/cookieStorage";
import { ReactComponent as CalendarClock } from "src/assets/icons/diagrams/CalendarClockIcon.svg";

const PAUSE_NOTIFICATION_TIME = 48 * 60 * 60 * 1000; // 48 hours in ms

const CalendlyScheduleOnboarding = () => {
  const location = useLocation();
  const { activeCompanyObject } = useCompanySelection();
  const contactId = activeCompanyObject?.contactId;
  const isPrimaryUser = activeCompanyObject?.isPrimary || false;
  const cookieKey = `${contactId || "_"}_calendlyscheduleonboarding_shown`;
  const hasCalendlyScheduleOnboardingShown = () => {
    const cookieValue = cookieStorage.getItemSync(cookieKey);
    return cookieValue === null ? false : cookieValue === "true";
  };
  const setHasCalendlyScheduleOnboardingShown = (value: boolean) => {
    cookieStorage.setItemSync(cookieKey, value.toString());
  };

  const pauseCookieKey = `${contactId || "_"}_calendlyscheduleonboarding_pause`;
  const isNotificationPaused = () => {
    const pauseUntil = cookieStorage.getItemSync(pauseCookieKey);
    if (!pauseUntil) {
      return false;
    }
    const now = Date.now();
    const pauseUntilTime = parseInt(pauseUntil, 10);

    return now < pauseUntilTime;
  };

  const getPauseUntil = () => {
    try {
      const override = localStorage.getItem("calendly-pause-until");
      if (override) {
        const parsed = parseInt(override, 10);
        if (!isNaN(parsed) && parsed > 0) {
          return Date.now() + parsed * 60 * 1000;
        }
      }
      return Date.now() + PAUSE_NOTIFICATION_TIME;
    } catch (err) {
      return Date.now() + PAUSE_NOTIFICATION_TIME;
    }
  };

  const pauseNotification = () => {
    const pauseUntil = getPauseUntil();
    cookieStorage.setItemSync(pauseCookieKey, pauseUntil.toString());
  };

  const { isOpen, onOpen, onClose } = useDisclosure();

  const handleCloseModal = () => {
    setHasCalendlyScheduleOnboardingShown(true);
    onClose();
  };

  const handlePauseNotification = () => {
    pauseNotification();
    onClose();
  };

  const AI_CALL_HANDLING_PAGE = "/abby-ai-receptionist/ai-call-handling";
  const AI_ONBOARDING_PAGE = "/abby-ai-receptionist/ai-receptionist-onboarding";
  const prevPathRef = useRef(location.pathname);

  const calendlyRef = useRef<HTMLDivElement>(null);
  const [widgetLoaded, setWidgetLoaded] = useState(false);
  const calendlyUrl = process.env.REACT_APP_CALENDLY_SCHEDULE_ONBOARD
    ? `${process.env.REACT_APP_CALENDLY_SCHEDULE_ONBOARD}?month=${new Date()
        .toISOString()
        .slice(0, 7)}`
    : null;

  const isValidUrl = (url?: string | null): boolean => {
    if (!url) return false;
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const handleInitWidget = () => {
    if (calendlyRef.current && (window as any).Calendly) {
      (window as any).Calendly.initInlineWidget({
        url: calendlyUrl,
        parentElement: calendlyRef.current,
      });
      setWidgetLoaded(true);
    }
  };

  const getModalWidth = () => {
    if (widgetLoaded) {
      return { base: "90%", md: "600px" };
    }
    return "578px";
  };

  const getModalHeigth = () => {
    return "max-content";
  };

  useEffect(() => {
    const existingScript = document.querySelector(
      'script[src*="calendly"]'
    ) as HTMLScriptElement | null;

    if (!existingScript) {
      const script = document.createElement("script");
      script.src = "https://assets.calendly.com/assets/external/widget.js";
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);

  useEffect(() => {
    if (!isPrimaryUser || !isValidUrl(calendlyUrl)) {
      return;
    }
    const prevPath = prevPathRef.current;
    const currentPath = location.pathname;
    if (
      AI_CALL_HANDLING_PAGE === prevPath &&
      AI_CALL_HANDLING_PAGE !== currentPath &&
      AI_ONBOARDING_PAGE !== currentPath &&
      "/login" !== currentPath &&
      !hasCalendlyScheduleOnboardingShown() &&
      !isNotificationPaused()
    ) {
      onOpen();
    }
    prevPathRef.current = currentPath;
  }, [location.pathname, onOpen]);

  useEffect(() => {
    const handler = () => {
      if (
        !hasCalendlyScheduleOnboardingShown() &&
        !isNotificationPaused() &&
        isValidUrl(calendlyUrl)
      ) {
        setWidgetLoaded(false);
        onOpen();
      }
    };

    window.addEventListener("aiOnboardingModalIsClosed", handler);
    return () =>
      window.removeEventListener("aiOnboardingModalIsClosed", handler);
  }, []);

  if (hasCalendlyScheduleOnboardingShown()) {
    return null;
  }

  return (
    <Modal
      isCentered
      isOpen={isOpen}
      onClose={handleCloseModal}
      closeOnOverlayClick={false}
    >
      <ModalOverlay
        bg={"blackAlpha.700"}
        backdropFilter={"auto"}
        backdropBlur={"5px"}
      />
      <ModalContent
        w={getModalWidth()}
        h={getModalHeigth()}
        maxW={"95%"}
        maxH={"95%"}
        className={"pendo-ai-calendlyscheduleonboarding-modal"}
      >
        <ModalCloseButton
          size={"lg"}
          _focus={{ boxShadow: "none", outline: "none" }}
          className={"pendo-ai-calendlyscheduleonboarding-modal-closebtn"}
        />
        <ModalBody
          px={widgetLoaded ? 0 : 14}
          pt={widgetLoaded ? 2 : 14}
          pb={widgetLoaded ? 2 : 8}
          overflow={"hidden"}
        >
          <Box
            ref={calendlyRef}
            minW={{ base: "100%", md: "300px" }}
            w={"100%"}
            maxW={{ base: "100%", md: "900px" }}
            h={widgetLoaded ? "700px" : "0px"}
            transition="height 0.3s ease"
          />
          {!widgetLoaded && (
            <Flex
              direction={"column"}
              justifyContent={"center"}
              alignItems={"center"}
              gap={3}
            >
              <Text
                as={"div"}
                fontWeight={"700"}
                fontSize={"20px"}
                color={"#0C2EA9"}
              >
                Need a little help?
              </Text>
              <Text
                as={"div"}
                fontWeight={"600"}
                fontSize={"sm"}
                textAlign={"center"}
                maxW={"368px"}
                pb={4}
              >
                Want a hand setting up your AI Receptionist? Our team can guide
                you through it.
              </Text>
              <Spacer />
              <Button
                variant={"purple"}
                w={"274px"}
                h={10}
                px={2}
                py={3}
                onClick={handleInitWidget}
                gap={2}
                className={
                  "pendo-ai-calendlyscheduleonboarding-modal-calendlybtn"
                }
              >
                <Icon
                  as={CalendarClock}
                  fontSize={"md"}
                  fill={"currentColor"}
                />
                Schedule Assisted Onboarding
              </Button>
              <Button
                variant={"text"}
                w={"274px"}
                h={10}
                px={2}
                py={3}
                onClick={handlePauseNotification}
                className={
                  "pendo-ai-calendlyscheduleonboarding-modal-cancelbtn"
                }
              >
                Not right now
              </Button>
            </Flex>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default CalendlyScheduleOnboarding;
